import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface VendorRating {
  id: string;
  rating: number;
  feedback: string | null;
  created_at: string;
  product: {
    id: string;
    title: string;
    images: string[] | null;
  };
  buyer: {
    id: string;
    username: string | null;
    full_name: string | null;
  };
  sale_date: string | null;
}

export interface VendorRatingsData {
  ratings: VendorRating[];
  averageRating: number | null;
  totalRatings: number;
}

export function useVendorRatings(vendorId: string | undefined) {
  return useQuery({
    queryKey: ['vendor-ratings', vendorId],
    queryFn: async (): Promise<VendorRatingsData> => {
      console.log('[useVendorRatings] Fetching ratings for vendor:', vendorId);
      
      if (!vendorId) {
        console.log('[useVendorRatings] No vendorId provided');
        return { ratings: [], averageRating: null, totalRatings: 0 };
      }

      // First, get all products for this vendor
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id')
        .eq('vendor_id', vendorId);
      
      console.log('[useVendorRatings] Products found:', products?.length || 0);

      if (productsError) {
        console.error('Error fetching vendor products:', productsError);
        throw productsError;
      }

      if (!products || products.length === 0) {
        return { ratings: [], averageRating: null, totalRatings: 0 };
      }

      const productIds = products.map(p => p.id);

      // Get all ratings for these products with product and user info
      const { data: ratings, error: ratingsError } = await supabase
        .from('product_ratings')
        .select(`
          id,
          rating,
          feedback,
          created_at,
          product_id,
          user_id,
          products:product_id (
            id,
            title,
            images
          )
        `)
        .in('product_id', productIds)
        .order('created_at', { ascending: false });

      if (ratingsError) {
        console.error('Error fetching ratings:', ratingsError);
        throw ratingsError;
      }

      console.log('[useVendorRatings] Ratings found:', ratings?.length || 0);

      if (!ratings || ratings.length === 0) {
        return { ratings: [], averageRating: null, totalRatings: 0 };
      }

      // Get user profiles for all raters
      const userIds = [...new Set(ratings.map(r => r.user_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, full_name')
        .in('id', userIds);
      
      console.log('[useVendorRatings] Profiles found:', profiles);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Get payment dates for sale dates
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('product_id, buyer_id, created_at')
        .in('product_id', productIds)
        .eq('status', 'completed');

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
      }

      // Create a map of product_id + buyer_id -> sale date
      const paymentMap = new Map(
        payments?.map(p => [`${p.product_id}-${p.buyer_id}`, p.created_at]) || []
      );

      // Transform the data
      const transformedRatings: VendorRating[] = ratings.map(r => ({
        id: r.id,
        rating: r.rating,
        feedback: r.feedback,
        created_at: r.created_at,
        product: {
          id: (r.products as any)?.id || r.product_id,
          title: (r.products as any)?.title || 'Unknown Product',
          images: (r.products as any)?.images || null,
        },
        buyer: {
          id: r.user_id,
          username: profilesMap.get(r.user_id)?.username || null,
          full_name: profilesMap.get(r.user_id)?.full_name || null,
        },
        sale_date: paymentMap.get(`${r.product_id}-${r.user_id}`) || null,
      }));

      // Calculate average rating
      const totalRating = ratings.reduce((sum, r) => sum + r.rating, 0);
      const averageRating = ratings.length > 0 ? totalRating / ratings.length : null;

      return {
        ratings: transformedRatings,
        averageRating,
        totalRatings: ratings.length,
      };
    },
    enabled: !!vendorId,
  });
}

